import moment from 'moment-timezone';

import { CHANNELS } from './config';
import { extractChannels, extractDeliveryPlatform } from './customLogic';
import { getChannelSales } from '@services/channelSales';
/**
 * Fetch and aggregate channel sales data for all stores
 */

export const fetchChannelData = async (
  storeIds,
  startDate,
  endDate,
  defaultChannelId,
  clientChannels,
) => {
  const currentData = await getChannelSales(
    storeIds,
    moment(startDate).format('YYYY-MM-DD'),
    moment(endDate).format('YYYY-MM-DD'),
  );

  return currentData
    .map((item) => ({
      ...item,
      analyticsChannel: extractChannels(item, defaultChannelId, clientChannels),
    }))
    .map((item) => ({
      ...item,
      deliveryPlatform: extractDeliveryPlatform(item),
    }));
};

/**
 * Transform API data into aggregated summary data
 */
export const transformToSummaryData = (apiData, metricType, isPercentage) => {
  if (!apiData?.current || !apiData?.previous) {
    return null;
  }

  // Calculate totals for current period
  const currentTotal = apiData.current.reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const currentDineIn = apiData.current
    .filter((item) => item.analyticsChannel === CHANNELS.DINE_IN)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const currentTakeaway = apiData.current
    .filter((item) => item.analyticsChannel === CHANNELS.TAKEAWAY)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const currentDelivery = apiData.current
    .filter((item) => item.analyticsChannel === CHANNELS.DELIVERY)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);

  // Calculate totals for previous period
  const previousTotal = apiData.previous.reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const previousDineIn = apiData.previous
    .filter((item) => item.analyticsChannel === CHANNELS.DINE_IN)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const previousTakeaway = apiData.previous
    .filter((item) => item.analyticsChannel === CHANNELS.TAKEAWAY)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);
  const previousDelivery = apiData.previous
    .filter((item) => item.analyticsChannel === CHANNELS.DELIVERY)
    .reduce((sum, item) => sum + (item[metricType] || 0), 0);

  if (!isPercentage) {
    return {
      total: { current: currentTotal, previous: previousTotal },
      eatin: { current: currentDineIn, previous: previousDineIn },
      takeaway: { current: currentTakeaway, previous: previousTakeaway },
      delivery: { current: currentDelivery, previous: previousDelivery },
    };
  }

  return {
    total: { current: currentTotal, previous: previousTotal },
    eatin: {
      current: currentTotal ? (currentDineIn / currentTotal) * 100 : null,
      previous: previousTotal ? (previousDineIn / previousTotal) * 100 : null,
    },
    takeaway: {
      current: currentTotal ? (currentTakeaway / currentTotal) * 100 : null,
      previous: previousTotal ? (previousTakeaway / previousTotal) * 100 : null,
    },
    delivery: {
      current: currentTotal ? (currentDelivery / currentTotal) * 100 : null,
      previous: previousTotal ? (previousDelivery / previousTotal) * 100 : null,
    },
  };
};

const calculatePercentage = (channelValue, totalValue) => ({
  current: totalValue.current ? (channelValue.current / totalValue.current) * 100 : null,
  previous: totalValue.previous ? (channelValue.previous / totalValue.previous) * 100 : null,
});

export const transformToTableData = (apiData, stores, metricType) => {
  if (!apiData?.current || !apiData?.previous || !stores) {
    return [];
  }

  const storesData = {};
  stores.forEach((store) => {
    storesData[store.id] = {
      id: store.id,
      storeName: store.name,
      eatin: { current: 0, previous: 0 },
      takeaway: { current: 0, previous: 0 },
      delivery: { current: 0, previous: 0 },
      total: { current: 0, previous: 0 },
      ubereats: { current: 0, previous: 0 },
      deliveroo: { current: 0, previous: 0 },
      other: { current: 0, previous: 0 },
    };
  });

  ['current', 'previous'].forEach((period) => {
    apiData[period].forEach((item) => {
      storesData[item.storeId][item.analyticsChannel][period] += item[metricType];
      storesData[item.storeId].total[period] += item[metricType];
      if (item.deliveryPlatform) {
        storesData[item.storeId][item.deliveryPlatform][period] += item[metricType];
      }
    });
  });

  const tableData = Object.values(storesData);

  tableData.forEach((storeData) => {
    storeData.eatinPercentage = calculatePercentage(storeData.eatin, storeData.total);
    storeData.takeawayPercentage = calculatePercentage(storeData.takeaway, storeData.total);
    storeData.deliveryPercentage = calculatePercentage(storeData.delivery, storeData.total);
    storeData.ubereatsPercentage = calculatePercentage(storeData.ubereats, storeData.delivery);
    storeData.deliverooPercentage = calculatePercentage(storeData.deliveroo, storeData.delivery);
    storeData.otherPercentage = calculatePercentage(storeData.other, storeData.delivery);
  });

  return tableData;
};
